import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Stop recording states for stop recording state machine
 * Only active when FEATURE_FLAGS.CONNECTION_RETRY is enabled
 */
export const STOP_RECORDING_STATE = {
  IDLE: 'idle',
  STOPPING: 'stopping',
  WAITING_TRANSCRIPT: 'waiting_transcript',
  SUCCESS: 'success',
  ERROR_STOPPING: 'error_stopping',
  ERROR_TRANSCRIPT: 'error_transcript',
} as const;

export type StopRecordingState =
  (typeof STOP_RECORDING_STATE)[keyof typeof STOP_RECORDING_STATE];

interface StopRecordingStoreState {
  state: StopRecordingState;
  errorMessage?: string;
  transcriptData?: {
    transcriptText: string;
  };

  setState: (
    state: StopRecordingState,
    errorMessage?: string,
    transcriptData?: { transcriptText: string }
  ) => void;
  reset: () => void;
}

const initialState = {
  state: STOP_RECORDING_STATE.IDLE as StopRecordingState,
  errorMessage: undefined,
  transcriptData: undefined,
};

/**
 * Zustand store for managing stop recording state machine
 * Tracks the state of the stop recording flow: stopping audio → waiting for transcript → success/error
 *
 * NOTE: This store is only used when FEATURE_FLAGS.CONNECTION_RETRY is enabled
 */
export const useStopRecordingStore = create<StopRecordingStoreState>()(
  devtools(
    (set) => ({
      ...initialState,

      setState: (
        newState: StopRecordingState,
        errorMessage?: string,
        transcriptData?: { transcriptText: string }
      ) => {
        return set(
          {
            state: newState,
            errorMessage,
            transcriptData,
          },
          false,
          'stopRecording/setState'
        );
      },

      reset: () => set(initialState, false, 'stopRecording/reset'),
    }),
    { name: 'stop-recording-store' }
  )
);
